import React, { useState, useEffect } from "react";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { useTranslation } from "react-i18next";

const daysOfWeek = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

export default function NutritionPlanTab({ athleteId }) {
  const { t, i18n } = useTranslation();
  const [plan, setPlan] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [expandedDay, setExpandedDay] = useState(null);

  useEffect(() => {
    const fetchPlan = async () => {
      const ref = doc(db, "nutritionPlans", athleteId, "plans", "weekly");
      const snapshot = await getDoc(ref);

      if (snapshot.exists()) {
        const data = snapshot.data();
        setPlan(data.plan || {});
        setLastUpdated(data.updatedAt || null);
      } else {
        const empty = {};
        daysOfWeek.forEach(day => {
          empty[day] = {
            calories: "",
            breakfast: "",
            lunch: "",
            dinner: "",
            snacks: "",
            notes: ""
          };
        });
        setPlan(empty);
        setIsEditing(true);
      }
    };

    if (athleteId) fetchPlan();
  }, [athleteId]);

  const handleChange = (day, field, value) => {
    setPlan(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field]: value
      }
    }));
  };

  const handleSave = async () => {
    const docRef = doc(db, "nutritionPlans", athleteId, "plans", "weekly");
    await setDoc(docRef, {
      plan,
      updatedAt: new Date().toISOString()
    });
    setIsEditing(false);
    setLastUpdated(new Date().toISOString());
  };

  const label = (key) => t(`nutrition_plan.${key}`) || key;

  return (
    <div className="bg-white p-6 rounded shadow" dir={i18n.language === "ar" ? "rtl" : "ltr"}>
      <h3 className="text-xl font-semibold mb-4">{t("nutrition_plan_title")}</h3>
      {lastUpdated && !isEditing && (
        <p className="text-sm text-gray-500 mb-4">
          {t("last_updated")}: {new Date(lastUpdated).toLocaleDateString()}
        </p>
      )}

      {/* Day Cards */}
      <div className="space-y-4">
        {daysOfWeek.map(day => (
          <div key={day} className="border rounded-lg shadow-sm">
            {/* Day Header */}
            <button
              onClick={() => setExpandedDay(expandedDay === day ? null : day)}
              className="w-full text-left px-4 py-3 bg-[#f1efe7] flex justify-between items-center rounded-t-lg hover:bg-[#e9e6de]"
            >
              <span className="font-semibold text-[#b22222] text-md">{t(`days.${day}`)}</span>
              <span className="text-sm text-gray-600">
                {plan[day]?.calories ? `${plan[day].calories} kcal` : t("no_data")}
              </span>
            </button>

            {/* Day Content */}
            {expandedDay === day && (
              <div className="p-4 space-y-4 bg-white rounded-b-lg border-t">
                {/* Top Row */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">{label("calories")} (kcal)</label>
                    <input
                      type="number"
                      value={plan[day]?.calories || ""}
                      onChange={(e) => handleChange(day, "calories", e.target.value)}
                      className={`w-full px-4 py-2 border border-gray-300 rounded-md ${!isEditing ? "bg-gray-100" : "bg-white"} focus:ring-2 focus:ring-[#b22222]`}
                      disabled={!isEditing}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">{label("notes")}</label>
                    <textarea
                      rows={1}
                      value={plan[day]?.notes || ""}
                      onChange={(e) => handleChange(day, "notes", e.target.value)}
                      className={`w-full px-4 py-2 border border-gray-300 rounded-md ${!isEditing ? "bg-gray-100" : "bg-white"} focus:ring-2 focus:ring-[#b22222]`}
                      disabled={!isEditing}
                    />
                  </div>
                </div>

                {/* Meals */}
                {["breakfast", "lunch", "dinner", "snacks"].map(field => (
                  <div key={field}>
                    <label className="block text-sm font-medium mb-1">{label(field)}</label>
                    <textarea
                      rows={2}
                      value={plan[day]?.[field] || ""}
                      onChange={(e) => handleChange(day, field, e.target.value)}
                      className={`w-full px-4 py-2 border border-gray-300 rounded-md ${!isEditing ? "bg-gray-100" : "bg-white"} focus:ring-2 focus:ring-[#b22222]`}
                      disabled={!isEditing}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Buttons */}
      <div className="mt-6 flex gap-4">
        {isEditing ? (
          <button
            onClick={handleSave}
            className="bg-[#b22222] text-white px-6 py-2 rounded hover:bg-red-800 transition"
          >
            {t("save")}
          </button>
        ) : (
          <button
            onClick={() => setIsEditing(true)}
            className="bg-gray-100 text-gray-800 px-6 py-2 rounded hover:bg-gray-200 transition"
          >
            {t("edit")}
          </button>
        )}
      </div>
    </div>
  );
}
